import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { contactsService } from '../../services/contactsService';
import { salesService } from '../../services/salesService';
import { purchasesService } from '../../services/purchasesService';
import {
  PhoneIcon,
  MapPinIcon,
  CurrencyRupeeIcon,
  ArchiveBoxIcon,
  ClockIcon,
  ShoppingBagIcon,
  TruckIcon,
  ArrowLeftIcon,
  CurrencyDollarIcon
} from '@heroicons/react/24/outline';
import DataTable from '../common/DataTable';
import StatCard from '../common/StatCard';
import AddContactModal from './AddContactModal';
import BulkPaymentModal from './BulkPaymentModal';
import { Tab } from '@headlessui/react';
import { paymentsService } from '../../services/paymentsService';
import Modal from '../common/Modal';

function classNames(...classes) {
  return classes.filter(Boolean).join(' ');
}

export default function ContactDetails() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [contact, setContact] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isPaymentModalOpen, setIsPaymentModalOpen] = useState(false);
  const [payments, setPayments] = useState([]);
  const [activeTab, setActiveTab] = useState(0);
  const [isPaymentHistoryOpen, setIsPaymentHistoryOpen] = useState(false);

  // Helper functions defined before they're used
  const calculateMonthlyAverage = (transactions) => {
    if (!transactions?.length) return 0;

    const totalAmount = transactions.reduce((sum, t) => sum + (t.totalAmount || 0), 0);
    const firstTransaction = new Date(transactions[transactions.length - 1].createdAt);
    const lastTransaction = new Date(transactions[0].createdAt);

    const monthsDiff = (lastTransaction.getFullYear() - firstTransaction.getFullYear()) * 12 +
      (lastTransaction.getMonth() - firstTransaction.getMonth()) + 1;

    return Math.round(totalAmount / Math.max(1, monthsDiff));
  };

  const calculateTotalTransactions = (transactions) => {
    if (!transactions?.length) return 0;
    return transactions.reduce((sum, t) => sum + (t.totalAmount || 0), 0);
  };

  // Stats config defined after helper functions
  const statsConfig = [
    {
      title: 'Total Balance',
      value: `₹${contact?.balance?.toLocaleString() || 0}`,
      icon: CurrencyRupeeIcon,
      iconBgColor: contact?.balance > 0 ? 'bg-red-500' : 'bg-green-500'
    },
    ...(contact?.contactType === 'customer' ? [{
      title: 'Crates Out',
      value: contact?.cratesOut || 0,
      icon: ArchiveBoxIcon,
      iconBgColor: 'bg-blue-500'
    }] : []),
    {
      title: 'Monthly Average',
      value: `₹${calculateMonthlyAverage(contact?.transactions).toLocaleString()}`,
      icon: ClockIcon,
      iconBgColor: 'bg-purple-500'
    },
    {
      title: contact?.contactType === 'customer' ? 'Total Sales' : 'Total Purchases',
      value: `₹${calculateTotalTransactions(contact?.transactions).toLocaleString()}`,
      icon: contact?.contactType === 'customer' ? ShoppingBagIcon : TruckIcon,
      iconBgColor: 'bg-green-500'
    }
  ];

  useEffect(() => {
    const fetchContactDetails = async () => {
      setLoading(true);
      try {
        const details = await contactsService.getContactDetails(id);
        // Fetch transactions based on contact type
        const transactions = details.contactType === 'customer'
          ? await salesService.getOrdersByCustomer(id)
          : await purchasesService.getPurchasesBySupplier(id);

        setContact({
          ...details,
          transactions: transactions
        });
      } catch (error) {
        console.error('Error fetching contact details:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchContactDetails();
  }, [id]);

  useEffect(() => {
    const fetchPayments = async () => {
      if (contact?.id && isPaymentHistoryOpen) {
        try {
          const paymentHistory = await paymentsService.getCustomerPayments(contact.id);
          setPayments(paymentHistory);
        } catch (error) {
          console.error('Error fetching payment history:', error);
        }
      }
    };

    fetchPayments();
  }, [contact?.id, isPaymentHistoryOpen]);

  const handleContactEdited = async (updatedContact) => {
    try {
      // Refresh contact data after edit
      const details = await contactsService.getContactDetails(id);
      const transactions = details.contactType === 'customer'
        ? await salesService.getOrdersByCustomer(id)
        : await purchasesService.getPurchasesBySupplier(id);

      setContact({
        ...details,
        transactions: transactions
      });
    } catch (error) {
      console.error('Error refreshing contact details:', error);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  if (!contact) {
    return <div>Contact not found</div>;
  }

  const transactionColumns = [
    {
      header: 'Date',
      accessorKey: contact?.contactType === 'customer' ? 'finalizedAt' : 'createdAt',
      cell: (info) => new Date(info.getValue()).toLocaleDateString('en-GB')
    },
    {
      header: 'Items',
      accessorKey: 'items',
      cell: (info) => (
        <div className="max-w-[200px] truncate">
          {info.getValue()?.map(item =>
            `${item.vegetableName || item.vegetable} (${contact?.contactType === 'customer' ?
              item.actualQuantity : item.quantity}kg)`
          ).join(', ')}
        </div>
      )
    },
    {
      header: 'Amount',
      accessorKey: 'totalAmount',
      cell: (info) => `₹${info.getValue()?.toLocaleString()}`
    },
    {
      header: contact?.contactType === 'customer' ? 'Amount Received' : 'Amount Paid',
      accessorKey: contact?.contactType === 'customer' ? 'amountReceived' : 'amountPaid',
      cell: (info) => `₹${info.getValue()?.toLocaleString() || 0}`
    },
    {
      header: 'Balance',
      accessorKey: 'remainingBalance',
      cell: (info) => (
        <span className={`inline-flex rounded-full px-2 text-xs font-semibold leading-5 ${Number(info.getValue()) > 0
            ? 'bg-red-100 text-red-800'
            : 'bg-green-100 text-green-800'
          }`}>
          ₹{info.getValue()?.toLocaleString() || 0}
        </span>
      )
    }
  ];

  return (
    <div className="">
      <div className="flex justify-between items-center mb-6">
        <button
          onClick={() => navigate(-1)}
          className="inline-flex items-center px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-200 hover:text-gray-900 dark:hover:text-white"
        >
          <ArrowLeftIcon className="h-5 w-5 mr-2" />
          Back
        </button>

        <div className="flex gap-2">
          <button
            onClick={() => setIsPaymentHistoryOpen(true)}
            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium 
              rounded-md shadow-sm text-white bg-primary-600 hover:bg-primary-700 
              focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
          >
            <CurrencyDollarIcon className="h-5 w-5 mr-2" />
            Payment History
          </button>
          <button
            onClick={() => setIsEditModalOpen(true)}
            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium 
              rounded-md shadow-sm text-white bg-primary-600 hover:bg-primary-700 
              focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
          >
            Edit Contact
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {/* Contact Header */}
        <div className="bg-white dark:bg-[#2E3B4E] shadow rounded-lg p-6 md:row-span-1">
          <div className="flex flex-col h-full">

            {/* Contact Info */}
            <div className="flex-1">
              <div className="text-center mb-6">
                <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
                  {contact.name}
                </h1>
                <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200">
                  {contact.contactType === 'customer' ? 'Customer' : 'Supplier'}
                </span>
              </div>

              <div className="space-y-4">
                <div className="flex items-center p-3 bg-gray-50 dark:bg-gray-800 rounded-lg">
                  <PhoneIcon className="flex-shrink-0 h-5 w-5 text-gray-500 dark:text-gray-400" />
                  <div className="ml-3">
                    <p className="text-xs font-medium text-gray-500 dark:text-gray-400">Phone</p>
                    <p className="text-sm text-gray-900 dark:text-white">{contact.phone}</p>
                  </div>
                </div>

                <div className="flex items-center p-3 bg-gray-50 dark:bg-gray-800 rounded-lg">
                  <MapPinIcon className="flex-shrink-0 h-5 w-5 text-gray-500 dark:text-gray-400" />
                  <div className="ml-3">
                    <p className="text-xs font-medium text-gray-500 dark:text-gray-400">Address</p>
                    <p className="text-sm text-gray-900 dark:text-white">{contact.address}</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Add Payment Button */}
            {contact?.contactType === 'customer' && contact?.balance > 0 && (
              <div className="mt-6 pt-6 border-t border-gray-200 dark:border-gray-700">
                <button
                  onClick={() => setIsPaymentModalOpen(true)}
                  className="w-full inline-flex items-center justify-center px-4 py-2 border border-transparent 
                    text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 
                    focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                >
                  <CurrencyDollarIcon className="h-5 w-5 mr-2" />
                  Make Payment (₹{contact.balance.toLocaleString()})
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 md:col-span-2 gap-6 auto-rows-fr">
          {statsConfig.map((stat) => (
            <StatCard
              key={stat.title}
              title={stat.title}
              value={stat.value}
              icon={stat.icon}
              iconBgColor={stat.iconBgColor}
            />
          ))}
        </div>
      </div>

      {/* Transactions Table */}
      <div className="bg-white dark:bg-[#2E3B4E] shadow rounded-lg overflow-hidden mt-6">
        <div className="px-3 py-3 sm:px-6 border-b border-gray-200 dark:border-gray-700">
          <h3 className="text-lg leading-6 font-medium text-gray-900 dark:text-white">
            Transaction History
          </h3>
        </div>
        <DataTable
          data={contact.transactions || []}
          columns={transactionColumns}
          searchPlaceholder="Search transactions..."
        />
      </div>



      {/* Add Modal */}
      {contact && (
        <AddContactModal
          isOpen={isEditModalOpen}
          onClose={() => setIsEditModalOpen(false)}
          contactType={contact.contactType === 'customer' ? 'Customer' : 'Supplier'}
          onContactAdded={handleContactEdited}
          editMode={true}
          initialData={{
            name: contact.name,
            phone: contact.phone,
            address: contact.address,
          }}
          contactId={id}
        />
      )}

      {/* Add BulkPaymentModal */}
      {contact && (
        <BulkPaymentModal
          isOpen={isPaymentModalOpen}
          onClose={() => setIsPaymentModalOpen(false)}
          contact={contact}
          onPaymentComplete={handleContactEdited}
        />
      )}

      {/* Add Payment History Modal */}
      <Modal
        isOpen={isPaymentHistoryOpen}
        onClose={() => setIsPaymentHistoryOpen(false)}
        title="Payment History"
      >
        <div className="space-y-4">
          <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-4 mb-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-sm text-gray-500 dark:text-gray-400">Customer Name</p>
                <p className="text-lg font-medium text-gray-900 dark:text-white">{contact?.name}</p>
              </div>
              <div>
                <p className="text-sm text-gray-500 dark:text-gray-400">Current Balance</p>
                <p className={`text-lg font-medium ${contact?.balance > 0 ? 'text-red-600 dark:text-red-400' : 'text-green-600 dark:text-green-400'
                  }`}>
                  ₹{contact?.balance?.toLocaleString() || 0}
                </p>
              </div>
            </div>
          </div>

          <DataTable
            data={payments}
            columns={[
              {
                header: 'Date',
                accessorKey: 'createdAt',
                cell: (info) => new Date(info.getValue()).toLocaleDateString('en-GB')
              },
              {
                header: 'Amount',
                accessorKey: 'amount',
                cell: (info) => `₹${info.getValue()?.toLocaleString()}`
              },
              {
                header: 'Order Details',
                accessorKey: 'orderDetails',
                cell: (info) => {
                  const details = info.getValue();
                  if (info.row.original.type === 'bulk_payment') {
                    return (
                      <div className="max-w-[300px] truncate">
                        Applied to {details.orders?.length} orders
                      </div>
                    );
                  }
                  return (
                    <div className="max-w-[300px] truncate">
                      {details?.items?.map(item =>
                        `${item.vegetableName || item.vegetable} (${item.actualQuantity}kg)`
                      ).join(', ')}
                    </div>
                  );
                }
              }
            ]}
            searchPlaceholder="Search payments..."
          />
        </div>
      </Modal>
    </div>
  );
}